/**
 * Schedules API service
 * Handles all schedule-related API requests
 */

import { get, post, patch, del } from './api';
import type { Schedule, CreateScheduleInput, UpdateScheduleInput } from '@/types/schedule';
import type { Job } from '@/types/job';

/**
 * API endpoints
 */
const ENDPOINTS = {
  SCHEDULES: '/schedules',
  SCHEDULE: (id: string) => `/schedules/${id}`,
  PAUSE: (id: string) => `/schedules/${id}/pause`,
  RESUME: (id: string) => `/schedules/${id}/resume`,
  TRIGGER: (id: string) => `/schedules/${id}/trigger`,
  JOBS: (id: string) => `/schedules/${id}/jobs`,
};

/**
 * Get all schedules
 */
export async function getSchedules(): Promise<Schedule[]> {
  const response = await get<{ data: Schedule[] }>(ENDPOINTS.SCHEDULES);
  return response.data;
}

/**
 * Get a single schedule by ID
 */
export async function getSchedule(id: string): Promise<Schedule> {
  return get<Schedule>(ENDPOINTS.SCHEDULE(id));
}

/**
 * Create a new schedule
 */
export async function createSchedule(input: CreateScheduleInput): Promise<Schedule> {
  return post<Schedule, CreateScheduleInput>(ENDPOINTS.SCHEDULES, input);
}

/**
 * Update a schedule
 */
export async function updateSchedule(id: string, input: UpdateScheduleInput): Promise<Schedule> {
  return patch<Schedule, UpdateScheduleInput>(ENDPOINTS.SCHEDULE(id), input);
}

/**
 * Pause a schedule
 */
export async function pauseSchedule(id: string): Promise<Schedule> {
  return patch<Schedule>(ENDPOINTS.PAUSE(id), {});
}

/**
 * Resume a schedule
 */
export async function resumeSchedule(id: string): Promise<Schedule> {
  return patch<Schedule>(ENDPOINTS.RESUME(id), {});
}

/**
 * Trigger immediate run
 */
export async function triggerSchedule(id: string): Promise<Schedule> {
  return patch<Schedule>(ENDPOINTS.TRIGGER(id), {});
}

/**
 * Delete a schedule (soft-delete)
 */
export async function deleteSchedule(id: string): Promise<void> {
  return del<void>(ENDPOINTS.SCHEDULE(id));
}

/**
 * Get jobs spawned by a schedule
 */
export async function getScheduleJobs(id: string): Promise<Job[]> {
  const response = await get<{ data: Job[] }>(ENDPOINTS.JOBS(id));
  return response.data;
}
